import { useState, useEffect } from 'react';
import { User, Briefcase, Armchair, ArrowLeft } from 'lucide-react';
import { palette } from '../../styles/tokens';
import { SlidePanel } from '../common/SlidePanel';
import { MoneyInput } from '../common/MoneyInput';
import { useCreateTeamMember } from '../../hooks/useCreateTeamMember';
import { useAuthStore } from '../../stores/authStore';

type TeamType = 'employee' | 'contractor' | 'operator' | null;

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void;
  teamTotal: number;
}

const TYPE_OPTIONS: { key: TeamType & string; icon: typeof User; title: string; subtitle: string }[] = [
  { key: 'employee', icon: User, title: 'Employee', subtitle: 'Full team member on payroll' },
  { key: 'contractor', icon: Briefcase, title: 'Contractor', subtitle: 'Independent worker' },
  { key: 'operator', icon: Armchair, title: 'Operator', subtitle: 'Booth or chair renter' },
];

const TIER_LIMITS: Record<string, number | null> = { free: 5, trial: 10, starter: 10, growth: 30, business: null };
const LABEL: React.CSSProperties = { fontSize: 13, fontWeight: 500, color: palette.g500, marginBottom: 4, display: 'block' };
const INPUT: React.CSSProperties = { width: '100%', padding: '9px 12px', borderRadius: 8, border: `1px solid ${palette.g200}`, fontSize: 13.5, color: palette.g800, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' as const };
const FIELD: React.CSSProperties = { marginBottom: 16 };

export function AddToTeamPanel({ isOpen, onClose, onCreated, teamTotal }: Props) {
  const business = useAuthStore(s => s.business);
  const currentUser = useAuthStore(s => s.user);
  const { createTeamMember, loading, error } = useCreateTeamMember();
  const [selected, setSelected] = useState<TeamType>(null);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState('employee');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [hourlyRate, setHourlyRate] = useState('');

  const tier = business?.tier ?? 'free';
  const limit = TIER_LIMITS[tier];
  const atLimit = limit !== null && teamTotal >= limit;

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (isOpen) {
      setSelected(null);
      setFirstName(''); setLastName(''); setRole('employee');
      setPhone(''); setEmail(''); setHourlyRate('');
    }
  }, [isOpen]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const handleClose = () => { setSelected(null); onClose(); };

  const handleSubmit = async () => {
    if (!business || !selected || !currentUser) return;
    const ok = await createTeamMember({
      business_id: business.id, first_name: firstName, last_name: lastName,
      role, employment_type: selected,
      phone: phone || undefined, email: email || undefined,
      // Q1.5 boundary: hook accepts number; hourlyRate is canonical after MoneyInput blur.
      hourly_rate: hourlyRate ? Number(hourlyRate) : undefined,
      logged_by_user_id: currentUser.id,
    }, tier);
    if (ok) { handleClose(); onCreated(); }
  };

  const panelTitle = selected ? `New ${selected.charAt(0).toUpperCase() + selected.slice(1)}` : 'Add to Team';

  return (
    <SlidePanel isOpen={isOpen} onClose={handleClose} title={panelTitle} subtitle={selected ? `Team \u203A ${panelTitle}` : 'What type of team member?'}>
      {selected && (
        <button onClick={() => setSelected(null)} style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 16, padding: 0, border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 13, color: palette.g400, fontFamily: 'inherit' }}>
          <ArrowLeft size={15} /> Back to options
        </button>
      )}

      {/* Step 1: Type selection */}
      {!selected && (
        <div>
          <div style={{ padding: '8px 12px', borderRadius: 6, background: palette.g100, fontSize: 12, color: palette.g500, marginBottom: 16 }}>
            {teamTotal} of {limit ?? '\u221E'} team members ({tier} plan)
          </div>
          {atLimit && (
            <div style={{ padding: '10px 14px', borderRadius: 8, background: palette.amberWash, borderLeft: `3px solid ${palette.amber}`, color: palette.amber, fontSize: 13, fontWeight: 500, marginBottom: 16 }}>
              Team limit reached. Upgrade your plan to add more members.
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {TYPE_OPTIONS.map(opt => {
              const Icon = opt.icon;
              return (
                <div key={opt.key} onClick={() => !atLimit && setSelected(opt.key as TeamType)}
                  style={{ display: 'flex', alignItems: 'center', gap: 14, padding: 16, borderRadius: 10, border: `1px solid ${palette.g150}`, cursor: atLimit ? 'not-allowed' : 'pointer', opacity: atLimit ? 0.5 : 1, transition: 'all 0.12s' }}
                  onMouseEnter={e => { if (!atLimit) { e.currentTarget.style.background = palette.accentWash; e.currentTarget.style.borderColor = palette.accent; } }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = palette.g150; }}>
                  <Icon size={24} color={palette.g500} />
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 600, color: palette.g800 }}>{opt.title}</div>
                    <div style={{ fontSize: 13, color: palette.g400, marginTop: 1 }}>{opt.subtitle}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Step 2: Form */}
      {selected && (
        <div>
          {error && <div style={{ padding: '8px 12px', borderRadius: 6, background: palette.redWash, color: palette.red, fontSize: 12.5, marginBottom: 16 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 12, ...FIELD }}>
            <div style={{ flex: 1 }}><label style={LABEL}>First name *</label><input style={INPUT} value={firstName} onChange={e => setFirstName(e.target.value)} /></div>
            <div style={{ flex: 1 }}><label style={LABEL}>Last name *</label><input style={INPUT} value={lastName} onChange={e => setLastName(e.target.value)} /></div>
          </div>
          {currentUser?.role === 'owner' && selected === 'employee' && (
            <div style={FIELD}><label style={LABEL}>Role</label><select style={INPUT} value={role} onChange={e => setRole(e.target.value)}><option value="employee">Employee</option><option value="manager">Manager</option></select></div>
          )}
          <div style={FIELD}><label style={LABEL}>Phone</label><input style={INPUT} value={phone} onChange={e => setPhone(e.target.value)} /></div>
          <div style={FIELD}><label style={LABEL}>Email</label><input type="email" style={INPUT} value={email} onChange={e => setEmail(e.target.value)} /></div>
          <div style={FIELD}><label style={LABEL}>Hourly rate</label><MoneyInput value={hourlyRate} onChange={setHourlyRate} /></div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', paddingTop: 16, borderTop: `1px solid ${palette.g100}` }}>
            <button onClick={handleClose} style={{ padding: '8px 18px', borderRadius: 8, border: `1px solid ${palette.g200}`, background: palette.white, fontSize: 13.5, fontWeight: 500, color: palette.g600, cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
            <button onClick={handleSubmit} disabled={loading} style={{ padding: '8px 22px', borderRadius: 8, border: 'none', background: loading ? palette.g300 : palette.accent, color: palette.white, fontSize: 13.5, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}>{loading ? 'Adding...' : `Add ${selected.charAt(0).toUpperCase() + selected.slice(1)}`}</button>
          </div>
        </div>
      )}
    </SlidePanel>
  );
}
